import React from "react";
import {
  Box,
  FormControl,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import { SearchIcon } from "assets";
import { COLORS } from "components/consts";
import { EsgSingleSelectControlledField } from "components";
import type { TargetLevel } from "../types";
import { entitySelectSx } from "../styles";
import type { EntitySelectState } from "../hooks";
import { LabelWithTooltip } from "./LabelWithTooltip";

export interface EntitySelectFieldProps {
  targetLevel: TargetLevel | "";
  entity: string;
  onEntityChange: (value: string) => void;
  entityState: EntitySelectState;
}

export const EntitySelectField = ({
  targetLevel,
  entity,
  onEntityChange,
  entityState,
}: EntitySelectFieldProps) => {
  const {
    displayedOptions,
    hasMoreItems,
    remainingCount,
    searchText,
    handleSearchChange,
    handleLoadMore,
    handleMenuClose,
    handleMenuOpen,
    selectRef,
    menuOpen,
    selectWidth,
  } = entityState;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
      <LabelWithTooltip
        title="Entity"
        tooltipMessage="Available entities update based on selected level."
        required={targetLevel !== "Corporate"}
      />
      {!targetLevel || targetLevel === "Corporate" ? (
        <EsgSingleSelectControlledField
          selectedValue={entity}
          setSelectedValue={(v) => onEntityChange(v as string)}
          options={[]}
          placeholder="Select Entity"
          width="100%"
          label=""
          disabled
          required={false}
        />
      ) : (
        <FormControl ref={selectRef} fullWidth sx={{ width: "100%" }}>
          <Select
            sx={entitySelectSx}
            open={menuOpen}
            onOpen={handleMenuOpen}
            onClose={handleMenuClose}
            MenuProps={{
              autoFocus: false,
              PaperProps: {
                sx: {
                  maxHeight: 400,
                  borderRadius: "8px",
                  ...(selectWidth != null && {
                    width: selectWidth,
                    minWidth: selectWidth,
                  }),
                },
              },
              MenuListProps: {
                sx: { maxWidth: "100%", paddingTop: 0 },
              },
            }}
            displayEmpty
            value={entity || ""}
            renderValue={() => entity || "Select Entity"}
          >
            <ListSubheader
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 1,
                backgroundColor: COLORS.white,
                paddingTop: 1,
                paddingBottom: 1,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <TextField
                size="small"
                autoFocus
                placeholder="Search for an entity"
                fullWidth
                value={searchText}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                onChange={(e) => handleSearchChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== "Escape") e.stopPropagation();
                }}
                onClick={(e) => e.stopPropagation()}
              />
            </ListSubheader>
            {displayedOptions.length > 0 ? (
              <>
                {displayedOptions.map((opt) => (
                  <MenuItem
                    key={opt.value}
                    value={opt.value}
                    onClick={() => {
                      onEntityChange(opt.value);
                      handleMenuClose();
                    }}
                    sx={{
                      color: COLORS.Mainblue,
                      fontWeight: 500,
                      maxWidth: "100%",
                      "&:hover": { backgroundColor: COLORS.White_Lilac },
                      "&.Mui-selected": { backgroundColor: COLORS.White_Lilac },
                      "&.Mui-selected:hover": { backgroundColor: COLORS.White_Lilac },
                    }}
                  >
                    <Tooltip
                      title={opt.label}
                      placement="top"
                      enterDelay={400}
                      PopperProps={{ sx: { maxWidth: 320 } }}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: "block",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "100%",
                        }}
                      >
                        {opt.label}
                      </Box>
                    </Tooltip>
                  </MenuItem>
                ))}
                {hasMoreItems && (
                  <Box
                    component="div"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleLoadMore();
                    }}
                    sx={{
                      display: "flex",
                      justifyContent: "left",
                      alignItems: "center",
                      padding: "8px 16px",
                      cursor: "pointer",
                      fontWeight: 600,
                      fontSize: "14px",
                      color: COLORS.Mainblue,
                      "&:hover": { backgroundColor: COLORS.White_Lilac },
                    }}
                  >
                    Load more ({remainingCount} remaining)
                  </Box>
                )}
              </>
            ) : (
              <MenuItem disabled>
                {searchText ? "No entities found" : "No entities available"}
              </MenuItem>
            )}
          </Select>
        </FormControl>
      )}
    </Box>
  );
};
